// Phase 14e-b — destructive-action button + typed-name-match confirmation modal.
//
// Per the 14e-4 lock tier matrix: destructive actions (void invoice/payout,
// refund, future force-cycle-close) use this component. Operator must type
// the entityName (or a fixed token) verbatim before Submit enables — same
// pattern as GitHub repo delete, Stripe Dashboard refund, etc. TOTP
// step-up integration is implicit via the global MutationCache handler
// in TotpStepUpModalProvider — if the backend returns
// TotpStepUpRequiredError, the provider intercepts + the operator
// completes the step-up + the mutation auto-retries.
//
// Modal pattern mirrors 14e-a-round-2 (ARIA dialog + aria-modal +
// aria-labelledby + Escape close).

import { useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Field, Input, useFocusTrap } from '@truecare/ui';
import { isStepUpError, messageOf } from '@truecare/api-client';

export interface DangerButtonProps {
  /** Operator-recognizable entity identifier (e.g. "INV-2026W26-AB12CD-001").
   *  Displayed in the modal copy; used as confirmationToken default. */
  entityName: string;
  /** Modal title — e.g. "Void invoice", "Refund payment". */
  actionLabel: string;
  /** What the operator types to enable Submit. Defaults to entityName
   *  so the operator copies the row identifier. Override with a short
   *  token (e.g. "VOID", "REFUND") when entityName isn't recognizable. */
  confirmationToken?: string;
  /** Defaults to true; most destructive actions log a reason. */
  requireReason?: boolean;
  minReasonLength?: number;
  /** Optional extra inputs rendered ABOVE the typed-match gate. Used
   *  by Refund (amount input). Pass a ReactNode that manages its own
   *  state via the host; the host reads back via closure when
   *  onConfirm fires. */
  extraFields?: ReactNode;
  /** Called when operator confirms. The danger-button host is
   *  responsible for the actual mutation. */
  onConfirm: (input: { reason?: string }) => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  /** Button label (rendered before the modal opens). */
  children: ReactNode;
  'data-testid'?: string;
}

export function DangerButton({
  entityName,
  actionLabel,
  confirmationToken,
  requireReason = true,
  minReasonLength = 10,
  extraFields,
  onConfirm,
  loading,
  disabled,
  children,
  'data-testid': testId,
}: DangerButtonProps) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const expectedToken = confirmationToken ?? entityName;

  // Focus in on open, trap Tab, Escape-to-close, restore focus to the
  // trigger on close (H-F4). Replaces the prior Escape-only handler.
  useFocusTrap(panelRef, { active: open, onEscape: () => closeModal() });

  function closeModal(): void {
    setOpen(false);
    setTyped('');
    setReason('');
    setError(null);
  }

  const typedMatches = typed === expectedToken;
  const reasonOk = !requireReason || reason.length >= minReasonLength;
  const canSubmit = typedMatches && reasonOk && !submitting;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm({ reason: requireReason ? reason : undefined });
      closeModal();
    } catch (e) {
      // Step-up errors are handled by TotpStepUpModalProvider's global
      // MutationCache hook; the queued mutation auto-retries after
      // verification. Close the modal silently so the operator doesn't
      // see a stale "TOTP step-up required" alert + double-submit on
      // an already-replayed mutation.
      if (isStepUpError(e)) {
        closeModal();
      } else {
        // Surfaces both pre-mutate validation throws AND mutation errors
        // (e.g. backend 422). Modal stays open so operator can correct.
        // Audit #10 — messageOf() reads the backend's real Convention-03 reason
        // from error.body.message; e.message alone is the generic "API 4xx".
        setError(messageOf(e, 'Submission failed. Try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="danger"
        size="sm"
        onClick={() => setOpen(true)}
        loading={loading}
        disabled={disabled}
        data-testid={testId}
      >
        {children}
      </Button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="danger-button-modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
          data-testid="danger-button-modal"
        >
          <div className="w-full max-w-md rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
            <h2
              id="danger-button-modal-title"
              className="font-heading text-xl text-danger"
            >
              {actionLabel}
            </h2>

            <Alert tone="danger" className="mt-3">
              This action is irreversible. Type the identifier below to
              confirm; this prevents accidental destructive operations.
            </Alert>

            {error && (
              <Alert tone="danger" className="mt-3" data-testid="danger-button-error">
                {error}
              </Alert>
            )}

            {extraFields && (
              <div className="mt-4 flex flex-col gap-3">{extraFields}</div>
            )}

            <Field
              label={`Type "${expectedToken}" to confirm`}
              className="mt-4"
              error={
                typed.length > 0 && !typedMatches
                  ? 'Does not match — copy the identifier exactly.'
                  : undefined
              }
            >
              <Input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={expectedToken}
                autoComplete="off"
                data-testid="danger-button-token-input"
              />
            </Field>

            {requireReason && (
              <Field
                label={`Reason (≥${minReasonLength} characters)`}
                className="mt-4"
              >
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full rounded-md border border-border bg-cream p-2 text-sm"
                  data-testid="danger-button-reason"
                />
              </Field>
            )}

            <div className="mt-5 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={!canSubmit}
                loading={submitting}
                onClick={() => void submit()}
                data-testid="danger-button-submit"
              >
                {actionLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
